import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import {
  Banner,
  BannerItem,
  CarouselHero,
  CarouselHeroItem,
  Container,
  EqualHeightProvider,
  Grid,
  GridColumn,
  IImageProps,
  Spinner,
  TBgColor,
  useCurrentBreakPoint
} from 'telia-front-react';
import { BannerData } from '../stores/BannersStore';
import { useStores } from '../stores/RootStore';
import { FetchStatus } from '../util/FetchStatus';
import JsxRenderer from './JsxRenderer';
import { normalizeId } from '../util/TextUtil';

interface SplitBannerProps {
  separated?: boolean;
}

const SplitBanner = observer((props: SplitBannerProps): JSX.Element => {
  const { bannersStore } = useStores();
  const currentBreakPoint = useCurrentBreakPoint();

  useEffect(() => {
    bannersStore.fetchBanners();
    bannersStore.subscribeToCustomerChangeReaction();

    return () => {
      bannersStore.unsubscribeToCustomerChangeReaction();
    };
  }, []);

  const getBannerImageProps = (altAttribute: string, index: number): IImageProps => {
    return {
      srcset: bannersStore.getMobileSrcSetForBanner(index),
      alt: altAttribute,
      align: 'center',
      sources: [
        {
          minWidth: '1000px',
          srcset: bannersStore.getDesktopSrcSetForBanner(index)
        }
      ]
    };
  };

  const getHeroItem = (bannerData: BannerData, index: number): JSX.Element => {
    return (
      <CarouselHeroItem
        key={`banner-hero-${index}`}
        background={bannerData.backgroundColor}
        image={bannerData.desktopImage && getBannerImageProps(bannerData.title, index)}
        theme={bannerData.backgroundColor ? 'dark' : null}
        priority={bannerData.priority}
        video={bannerData.video && { ...bannerData.video, defaultPlaying: true }}
      >
        <Container>
          <JsxRenderer key={index} jsx={bannerData.content} showErrorNotice={false} />
        </Container>
      </CarouselHeroItem>
    );
  };

  const getBanner = (bannerData: BannerData, index: number): JSX.Element => {
    const firstElement = index === 0;
    const separated = props.separated;
    return (
      <Banner
        noRadius={!separated}
        size={separated ? 'small' : 'large'}
        variant={bannerData.backgroundColor ? 'dark' : 'light'}
        background={bannerData.backgroundColor as TBgColor}
        minHeight={480}
        margin={
          separated ? { top: 'medium', right: firstElement ? 'xsmall' : 'medium', bottom: 'medium', left: firstElement ? 'medium' : 'xsmall' } : undefined
        }
      >
        <BannerItem
          id={normalizeId(`split-banner-item-${index.toString()}-${bannerData.title}`)}
          image={bannerData.desktopImage && getBannerImageProps(bannerData.title, index)}
        >
          <JsxRenderer key={index} jsx={bannerData.content} showErrorNotice={false} />
        </BannerItem>
      </Banner>
    );
  };

  const renderCarousel = (banners: BannerData[]): JSX.Element => {
    return <CarouselHero>{banners.map((banner: BannerData, index: number) => getHeroItem(banner, index))}</CarouselHero>;
  };

  const renderBanners = (banners: BannerData[]): JSX.Element => {
    return (
      <EqualHeightProvider groups={['heading', 'content', 'button']}>
        <Grid align={['start-xs']} gutter="none" style={{ marginTop: '-30px' }}>
          {banners.map((banner: BannerData, index: number) => (
            <GridColumn key={`banner-${index}`} width={['lg-6']}>
              {getBanner(banner, index)}
            </GridColumn>
          ))}
        </Grid>
      </EqualHeightProvider>
    );
  };

  if ([FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(bannersStore.status)) {
    return <Spinner />;
  }

  const banners = bannersStore.bannersToDisplay.slice(0, 2);

  return !currentBreakPoint || ['lg', 'xl', 'xxl'].includes(currentBreakPoint) ? renderBanners(banners) : renderCarousel(banners);
});

export default SplitBanner;
